import { jwtDecode } from 'jwt-decode';
import localStorageService from '../../services/localStorageService';
import EmployeeServices from '../../services/EmployeeService';
import LoginServices from '../../services/LoginService';

/**
 * Decode and validate JWT token
 * @param {string} token - JWT token to decode
 * @returns {object} Decoded token with validation info
 */
export const decodeAndValidateToken = (token) => {
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const isExpired = decoded.exp < currentTime;
    const expiresIn = decoded.exp - currentTime;

    return {
      decoded,
      isExpired,
      expiresIn,
      isValid: !isExpired && decoded.exp
    };
  } catch (error) {
    console.error('Token decode error:', error);
    return {
      decoded: null,
      isExpired: true,
      expiresIn: 0,
      isValid: false
    };
  }
};

/**
 * Store token data including expiration metadata
 */
export const storeTokenData = async (accessToken, refreshToken, user) => {
  const accessTokenData = decodeAndValidateToken(accessToken);
  const refreshTokenData = decodeAndValidateToken(refreshToken);

  const tokenMetadata = {
    accessToken,
    accessTokenExpiry: accessTokenData.decoded?.exp * 1000, // Convert to ms
    refreshToken,
    refreshTokenExpiry: refreshTokenData.decoded?.exp * 1000, // Convert to ms
    storedAt: Date.now(),
  };

  await localStorageService.setItem('access_token', accessToken);
  await localStorageService.setItem('refresh_token', refreshToken);
  await localStorageService.setItem('token_metadata', tokenMetadata);
  await localStorageService.setItem('user', user);

  // Set up token refresh interval
  setupTokenRefreshTimer(tokenMetadata.accessTokenExpiry);
};

/**
 * Setup automatic token refresh before expiration
 * Refreshes 5 minutes before actual expiration
 */
export const setupTokenRefreshTimer = (accessTokenExpiry) => {
  if (global.tokenRefreshTimer) {
    clearTimeout(global.tokenRefreshTimer);
  }

  const now = Date.now();
  const expiresIn = accessTokenExpiry - now;
  // Refresh 5 minutes (300000ms) before expiration
  const refreshTime = Math.max(expiresIn - 300000, 0);

  global.tokenRefreshTimer = setTimeout(() => {
    // Dispatch refresh action
    window.dispatchEvent(new Event('token-refresh-needed'));
  }, refreshTime);

  console.log(`Token refresh timer set for ${refreshTime}ms`);
};

/**
 * Refresh access token using refresh token
 */
export const refreshAccessToken = () => async (dispatch) => {
  try {
    const refreshToken = await localStorageService.getItem('refresh_token');

    if (!refreshToken) {
      dispatch({ type: 'AUTO_LOGOUT', payload: 'No refresh token available' });
      return;
    }

    // Check if refresh token is still valid
    const refreshTokenData = decodeAndValidateToken(refreshToken);

    if (refreshTokenData.isExpired) {
      console.log('Refresh token expired, logging out...');
      dispatch({ type: 'AUTO_LOGOUT', payload: 'Session expired. Please login again.' });
      return;
    }

    // Use the new LoginService to refresh token
    const response = await LoginServices.refreshToken(refreshToken);

    if (!response.success) {
      throw new Error(response.message || 'Token refresh failed');
    }

    const { user, accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

    await storeTokenData(newAccessToken, newRefreshToken, user);

    dispatch({
      type: 'TOKEN_REFRESHED',
      payload: { user, accessToken: newAccessToken, refreshToken: newRefreshToken },
    });

    console.log('Access token refreshed successfully');
  } catch (error) {
    console.error('Token refresh failed:', error);
    dispatch({ type: 'AUTO_LOGOUT', payload: 'Failed to refresh session' });
  }
};

/**
 * Login action with token management
 */
export const login = (email, password) => async (dispatch) => {
  try {
    // Call the login API
    const response = await LoginServices.login(email, password);

    if (!response.success) {
      throw new Error(response.error || response.message || 'Login failed');
    }

    const { user: apiUser, accessToken, refreshToken } = response.data;

    if (!apiUser.isActive || apiUser.Employee?.status === 'Inactive') {
      throw new Error('Your account is inactive. Please contact your administrator.');
    }

    // Extract user information from the API response
    const user = {
      id: apiUser.id,
      email: apiUser.email,
      firstName: apiUser.firstName,
      lastName: apiUser.lastName,
      emailVerified: apiUser.emailVerified,
      isActive: apiUser.isActive,
      employeeId: apiUser.employeeId,
      // Employee data from nested object
      companyId: apiUser.Employee?.companyId,
      type: apiUser.Employee?.type,
      status: apiUser.Employee?.status,
      contact_no: apiUser.Employee?.contact_no,
      employee: apiUser.Employee,
    };

    // Store token first so it's available for subsequent API calls
    await localStorageService.setItem('access_token', accessToken);

    // Fetch additional employee data with company information if needed
    let employeeData = null;
    let companyData = null;
    try {
      console.log('🔍 Fetching employee data for user ID:', user.employeeId);
      const employeeResponse = await EmployeeServices.getById(user.employeeId);

      console.log('📦 Full employee response:', employeeResponse);
      console.log('📦 Employee response.data:', employeeResponse?.data);

      // Handle different response structures
      employeeData = employeeResponse?.data?.view?.data || employeeResponse?.data;

      console.log('👤 Employee data extracted:', employeeData);

      if (employeeData) {
        // Extract company data
        companyData = employeeData.Company;

        console.log('🏢 Company data extracted:', companyData);

        if (companyData) {
          // Store company data in localStorage
          await localStorageService.setItem('company', companyData);
          console.log('✅ Company data stored in localStorage');
        } else {
          console.warn('⚠️ No Company data found in employee response');
        }

        // Enhance user object with additional employee details if available
        const enhancedUser = {
          ...user,
          companyId: employeeData.companyId || user.companyId,
          type: employeeData.type || user.type,
          status: employeeData.status || user.status,
          contact_no: employeeData.contact_no || user.contact_no,
          employee: employeeData,
        };

        console.log('✅ Enhanced user object:', enhancedUser);

        // Store with token metadata and setup refresh
        await storeTokenData(accessToken, refreshToken, enhancedUser);

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: enhancedUser, accessToken, refreshToken, company: companyData },
        });
      } else {
        console.warn('⚠️ No additional employee data found, using API response data');

        // Store with token metadata and setup refresh with API user data
        await storeTokenData(accessToken, refreshToken, user);

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, accessToken, refreshToken, company: companyData },
        });
      }
    } catch (employeeError) {
      console.error('❌ Failed to fetch additional employee data:', employeeError);
      console.error('❌ Error details:', employeeError?.response?.data || employeeError?.message);

      // Continue with login using API response data
      await storeTokenData(accessToken, refreshToken, user);

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, accessToken, refreshToken, company: companyData },
      });
    }
  } catch (error) {
    console.error('❌ Login failed:', error);
    dispatch({
      type: 'LOGIN_FAILURE',
      payload: error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Login failed',
    });
  }
};

/**
 * Logout action with cleanup
 */
export const logout = () => async (dispatch) => {
  try {
    const refreshToken = await localStorageService.getItem('refresh_token');
    if (refreshToken) {
      await LoginServices.logout(refreshToken);
    }
  } catch (e) {
    console.error('Logout service error:', e);
    // Continue with cleanup even if API call fails
  }

  // Clear timer
  if (global.tokenRefreshTimer) {
    clearTimeout(global.tokenRefreshTimer);
  }

  // Remove from localStorage
  await localStorageService.removeItem('access_token');
  await localStorageService.removeItem('refresh_token');
  await localStorageService.removeItem('token_metadata');
  await localStorageService.removeItem('user');
  await localStorageService.removeItem('company');

  dispatch({ type: 'LOGOUT' });
};

/**
 * Auto logout action (triggered when session expires)
 */
export const autoLogout = () => async (dispatch) => {
  try {
    const refreshToken = await localStorageService.getItem('refresh_token');
    if (refreshToken) {
      await LoginServices.logout(refreshToken);
    }
  } catch (e) {
    console.error('Auto logout error:', e);
    // Continue with cleanup even if API call fails
  }

  // Clear timer
  if (global.tokenRefreshTimer) {
    clearTimeout(global.tokenRefreshTimer);
  }

  // Remove from localStorage
  await localStorageService.removeItem('access_token');
  await localStorageService.removeItem('refresh_token');
  await localStorageService.removeItem('token_metadata');
  await localStorageService.removeItem('user');
  await localStorageService.removeItem('company');

  dispatch({
    type: 'AUTO_LOGOUT',
    payload: 'Your session has expired. Please login again.'
  });
};

/**
 * Check session validity on app load
 */
export const checkSessionValidity = () => async (dispatch) => {
  try {
    const accessToken = await localStorageService.getItem('access_token');
    const refreshToken = await localStorageService.getItem('refresh_token');

    if (!accessToken || !refreshToken) {
      return dispatch({ type: 'NO_SESSION' });
    }

    const accessTokenData = decodeAndValidateToken(accessToken);
    const refreshTokenData = decodeAndValidateToken(refreshToken);

    // Refresh token expired - auto logout
    if (refreshTokenData.isExpired) {
      return dispatch(autoLogout());
    }

    // Access token expired but refresh token valid - refresh
    if (accessTokenData.isExpired && !refreshTokenData.isExpired) {
      return dispatch(refreshAccessToken());
    }

    // Both tokens valid - setup refresh timer
    if (accessTokenData.isValid && refreshTokenData.isValid) {
      const user = await localStorageService.getItem('user');
      setupTokenRefreshTimer(accessTokenData.decoded.exp * 1000);

      return dispatch({
        type: 'SESSION_RESTORED',
        payload: { user, accessToken, refreshToken },
      });
    }

    dispatch({ type: 'NO_SESSION' });
  } catch (error) {
    console.error('Session check error:', error);
    dispatch({ type: 'NO_SESSION' });
  }
}; 